import React, { useEffect, useState, useRef } from "react";
import {
  ChevronDown,
  ChevronUp,
  Menu,
  X,
  ShoppingBag,
  LayoutGrid,
} from "lucide-react";

import { useNavigate, useLocation } from "react-router-dom";

import logo from "../assets/rigvelogo.png";
import goldlogo from "../assets/rigvelogogold.png";

export const Navigation = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ============================================
  // STATES
  // ============================================
  const [isOpen, setIsOpen] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);

  const [openMegaMenu, setOpenMegaMenu] = useState(false);

  const [mainCategories, setMainCategories] = useState([]);

  const [selectedMainCatId, setSelectedMainCatId] = useState(null);
  const [selectedCategoryData, setSelectedCategoryData] = useState();
  const [subCategories, setSubCategories] = useState([]);

  const [loading, setLoading] = useState(false);

  const [cartItemCount, setCartItemCount] = useState(0);

  const [extraData, setExtraData] = useState({});

  const megaMenuRef = useRef(null);
  // ============================================
  // FETCH EXTRA DETAILS
  // ============================================
  useEffect(() => {
    const fetchExtraData = async () => {
      try {
        const response = await fetch(
          "https://webadmin.rigve.com/api/get_extra_details",
        );

        const result = await response.json();

        if (result.status === 1) {
          setExtraData(result.data);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchExtraData();
  }, []);

  // ============================================
  // FETCH MAIN CATEGORY
  // ============================================
  useEffect(() => {
    const fetchMainCategory = async () => {
      try {
        const response = await fetch(
          "https://webadmin.rigve.com/api/get_main_category",
        );

        const result = await response.json();

        if (result.data) {
          setMainCategories(result.data);
        }
      } catch (error) {
        console.log(error);
      }
    };

    fetchMainCategory();
  }, []);

  // ============================================
  // FETCH SUB CATEGORY
  // ============================================
  useEffect(() => {
    if (!selectedMainCatId) return;

    const fetchSubCategory = async () => {
      setLoading(true);

      try {
        const response = await fetch(
          `https://webadmin.rigve.com/api/get_category?main_cat_id=${selectedMainCatId}`,
        );

        const result = await response.json();

        setSubCategories(result.data || []);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubCategory();
  }, [selectedMainCatId]);

  // ============================================
  // SCROLL EFFECT
  // ============================================
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // ============================================
  // CART COUNT
  // ============================================
  useEffect(() => {
    const existingCart = JSON.parse(localStorage.getItem("cartItems")) || [];

    setCartItemCount(existingCart.length);
  }, []);

  // ============================================
  // SCROLL SECTION
  // ============================================
  const handleScrollToSection = (id) => {
    if (location.pathname !== "/") {
      navigate("/");

      setTimeout(() => {
        const section = document.getElementById(id);

        if (section) {
          section.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 300);
    } else {
      const section = document.getElementById(id);

      if (section) {
        section.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    setIsOpen(false);
  };
  // ============================================
  // CLOSE DESKTOP MEGA MENU ON OUTSIDE CLICK
  // ============================================
  useEffect(() => {
    const handleOutsideClick = (event) => {
      // Desktop only
      if (window.innerWidth < 1024) return;

      if (
        openMegaMenu &&
        megaMenuRef.current &&
        !megaMenuRef.current.contains(event.target)
      ) {
        setOpenMegaMenu(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [openMegaMenu]);
  // ============================================
  // MENU ITEMS
  // ============================================
  const menuItems = [
    {
      label: "Home",
      type: "scroll",
      id: "home",
    },

    {
      label: "Services",
      type: "scroll",
      id: "services",
    },

    {
      label: "About Us",
      type: "scroll",
      id: "about",
    },

    {
      label: "Products",
      type: "mega",
    },

    {
      label: "Gallery",
      type: "page",
      href: "/gallery",
    },

    {
      label: "Contact",
      type: "page",
      href: "/contact",
    },

    {
      label: "Partner with Us",
      type: "page",
      href: "/partner",
    },
  ];

  // ============================================
  // AUTO SELECT FIRST CAT FOR DESKTOP MEGA MENU
  // ============================================
  useEffect(() => {
    if (
      openMegaMenu &&
      window.innerWidth >= 1024 &&
      !selectedMainCatId &&
      mainCategories.length > 0
    ) {
      setSelectedMainCatId(mainCategories[0].id);
    }
  }, [openMegaMenu, mainCategories, selectedMainCatId]);

  // ============================================
  // HANDLE MENU CLICK
  // ============================================
  const handleMenuClick = (item) => {
    if (item.type === "mega") {
      // Desktop toggle
      if (window.innerWidth >= 1024) {
        setOpenMegaMenu((prev) => {
          const nextState = !prev;
          if (nextState && !selectedMainCatId && mainCategories.length > 0) {
            setSelectedMainCatId(mainCategories[0].id);
          }
          return nextState;
        });
      } else {
        // Mobile toggle - start with all categories closed by default
        setOpenMegaMenu((prev) => {
          const nextState = !prev;
          if (nextState) {
            setSelectedMainCatId(null);
          }
          return nextState;
        });
      }

      return;
    }
    if (item.type === "scroll") {
      handleScrollToSection(item.id);

      return;
    }

    if (item.type === "page") {
      navigate(item.href);

      setIsOpen(false);

      setOpenMegaMenu(false);
    }
  };

  return (
    <>
      {/* ========================================= */}
      {/* NAVBAR */}
      {/* ========================================= */}
      <nav
        className={`sticky top-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "bg-black/90 backdrop-blur-xl shadow-xl"
            : "bg-white shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 relative">
          <div className="h-[75px] flex items-center justify-between gap-2">
            {/* ========================================= */}
            {/* LOGO */}
            {/* ========================================= */}
            <div
              className="cursor-pointer shrink-0"
              onClick={() => navigate("/")}
            >
              <img
                src={isScrolled ? goldlogo : logo}
                alt="logo"
                className="h-9 md:h-12 object-contain"
              />
            </div>

            {/* ========================================= */}
            {/* DESKTOP MENU */}
            {/* ========================================= */}
            <div className="hidden lg:flex items-center gap-3 xl:gap-8">
              {menuItems.map((item) => (
                <div
                  key={item.label}
                  className={item.type === "mega" ? "static" : "relative"}
                  ref={item.type === "mega" ? megaMenuRef : null}
                >
                  <button
                    onClick={() => handleMenuClick(item)}
                    className={`flex items-center gap-1 text-sm xl:text-base font-semibold transition-all duration-300 ${
                      isScrolled ? "text-white" : "text-gray-800"
                    } hover:text-orange-500 whitespace-nowrap`}
                  >
                    {item.label}

                    {item.type === "mega" &&
                      (openMegaMenu ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      ))}
                  </button>

                  {/* ========================================= */}
                  {/* DESKTOP MEGA MENU */}
                  {/* ========================================= */}
                  {item.type === "mega" && openMegaMenu && (
                    <div
                      className="
                      absolute left-1/2 top-[75px]
                      -translate-x-1/2
                      w-[95vw] lg:w-[950px] xl:w-[1150px]
                      max-w-[1200px]
                      bg-white rounded-[28px]
                      shadow-[0_20px_60px_rgba(0,0,0,0.15)]
                      border border-gray-100 overflow-hidden
                      z-50
                    "
                    >
                      <div
                        className="
                        grid grid-cols-1 lg:grid-cols-12
                        max-h-[85vh] overflow-hidden
                      "
                      >
                        {/* LEFT SIDE */}
                        <div
                          className="
                          lg:col-span-3
                          bg-gray-50
                          border-b lg:border-b-0 lg:border-r
                          border-gray-100
                          p-4 md:p-5
                          overflow-y-auto
                          max-h-[85vh]
                        "
                        >
                          <div className="flex items-center gap-3 mb-5">
                            <div className="bg-orange-100 p-2.5 rounded-2xl">
                              <LayoutGrid className="w-5 h-5 text-orange-600" />
                            </div>

                            <div>
                              <h2 className="font-bold text-base md:text-lg text-gray-900">
                                Categories
                              </h2>

                              <p className="text-xs md:text-sm text-gray-500">
                                Premium collections
                              </p>
                            </div>
                          </div>

                          <div className="space-y-2">
                            {mainCategories.map((item) => (
                              <button
                                key={item.id}
                                onMouseEnter={() =>
                                  window.innerWidth >= 1024 &&
                                  setSelectedMainCatId(item.id)
                                }
                                onClick={() => setSelectedMainCatId(item.id)}
                                className={`w-full text-left capitalize rounded-2xl
                                transition-all duration-300
                                p-3 ${
                                  selectedMainCatId === item.id
                                    ? "bg-orange-500 text-white shadow-lg"
                                    : "bg-white hover:bg-orange-50 text-gray-800"
                                }`}
                              >
                                <h3 className="font-normal line-clamp-1 text-sm">
                                  {item.name}
                                </h3>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* RIGHT SIDE */}
                        <div
                          className="
                          lg:col-span-9
                          p-4 md:p-6 lg:p-7
                          overflow-y-auto
                          max-h-[85vh]
                        "
                        >
                          <div className="mb-6">
                            <h2 className="text-lg md:text-2xl font-bold text-gray-900">
                              Collections
                            </h2>

                            <p className="text-xs md:text-sm text-gray-500 mt-1">
                              Explore decorative texture finishes
                            </p>
                          </div>

                          {loading ? (
                            <div className="flex items-center justify-center h-[300px]">
                              <p className="text-gray-500 text-sm md:text-base">
                                Loading...
                              </p>
                            </div>
                          ) : (
                            <div
                              className="
                              grid
                              grid-cols-1
                              sm:grid-cols-2
                              md:grid-cols-3
                              lg:grid-cols-3
                              xl:grid-cols-4
                              gap-4
                            "
                            >
                              {subCategories.map((collection) => (
                                <div
                                  key={collection.id}
                                  onClick={() => {
                                    navigate(
                                      `/collections/${collection.id}/${selectedMainCatId}`,
                                    );

                                    setOpenMegaMenu(false);

                                    setIsOpen(false);
                                  }}
                                  className="
                                  group cursor-pointer
                                  border border-gray-100
                                  rounded-xl
                                  hover:border-orange-200
                                  hover:bg-orange-50
                                  transition-all duration-300
                                "
                                >
                                  <div
                                    className="
                                    h-[120px] w-full
                                    rounded-t-xl
                                    overflow-hidden
                                    bg-gray-100
                                  "
                                  >
                                    <img
                                      src={collection.image}
                                      alt={collection.name}
                                      className="
                                      w-full h-full object-cover
                                      group-hover:scale-105
                                      transition-all duration-500
                                    "
                                    />
                                  </div>

                                  <div className="p-3">
                                    <h3
                                      className="
                                      font-semibold
                                      text-sm md:text-[14px]
                                      text-gray-900 text-center
                                      group-hover:text-orange-600
                                      transition-all duration-300
                                      line-clamp-1
                                    "
                                    >
                                      {collection.name}
                                    </h3>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* ========================================= */}
            {/* RIGHT SIDE */}
            {/* ========================================= */}
            <div className="flex items-center gap-2 xl:gap-3 shrink-0">
              {/* PHONE */}
              <a
                href={`tel:${extraData?.phone}`}
                className={`hidden lg:flex text-xs xl:text-sm font-semibold whitespace-nowrap ${
                  isScrolled ? "text-white" : "text-gray-800"
                }`}
              >
                Call : {extraData?.phone}
              </a>

              {/* CART */}
              <button
                onClick={() => navigate("/cart")}
                className={`relative p-2.5 sm:p-3 rounded-2xl transition-all duration-300 ${
                  isScrolled
                    ? "text-white hover:bg-white/10"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <ShoppingBag className="w-6 h-6" />

                {cartItemCount > 0 && (
                  <span
                    className="
                    absolute -top-1 -right-1
                    min-w-[20px] h-5 px-1 bg-red-500
                    text-white text-[10px] rounded-full
                    flex items-center justify-center
                  "
                  >
                    {cartItemCount}
                  </span>
                )}
              </button>

              {/* MOBILE MENU BUTTON */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="lg:hidden p-1"
              >
                {isOpen ? (
                  <X
                    className={`w-7 h-7 ${
                      isScrolled ? "text-white" : "text-black"
                    }`}
                  />
                ) : (
                  <Menu
                    className={`w-7 h-7 ${
                      isScrolled ? "text-white" : "text-black"
                    }`}
                  />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ========================================= */}
        {/* MOBILE MENU */}
        {/* ========================================= */}
        <div
          className={`
          lg:hidden fixed top-[75px] left-0 w-full
          bg-white z-50 transition-all duration-500
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
        >
          <div className="h-[calc(100vh-75px)] overflow-y-auto px-4 py-3 sm:py-4">
            {/* MENU ITEMS */}
            <div className="space-y-1.5 sm:space-y-2">
              {menuItems.map((item) => (
                <div key={item.label}>
                  <button
                    onClick={() => handleMenuClick(item)}
                    className="
                    w-full flex items-center justify-between
                    px-4 py-2.5 sm:py-3 rounded-xl bg-gray-50
                    hover:bg-orange-50 transition-all duration-300
                  "
                  >
                    <span className="font-semibold text-sm sm:text-base text-gray-800">
                      {item.label}
                    </span>

                    {item.type === "mega" && (
                      <ChevronDown
                        className={`w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-300 ${
                          openMegaMenu ? "rotate-180" : "rotate-0"
                        }`}
                      />
                    )}
                  </button>

                  {/* ========================================= */}
                  {/* MOBILE MEGA MENU WITH SMOOTH ACCORDION */}
                  {/* ========================================= */}
                  {item.type === "mega" && (
                    <div
                      className={`accordion-grid ${
                        openMegaMenu ? "accordion-open mt-2" : ""
                      }`}
                    >
                      <div className="accordion-inner">
                        <div
                          className="
                          bg-gray-50
                          rounded-2xl
                          p-3
                          max-h-[60vh]
                          overflow-y-auto
                        "
                        >
                          {/* VERTICAL MAIN CATEGORIES & SUB CATEGORIES */}
                          <div className="flex flex-col gap-2">
                            {mainCategories.map((mainCat) => {
                              const isSelected =
                                selectedMainCatId === mainCat.id;

                              return (
                                <div key={mainCat.id} className="w-full">
                                  <button
                                    onClick={() =>
                                      setSelectedMainCatId(
                                        isSelected ? null : mainCat.id,
                                      )
                                    }
                                    className={`
                                      w-full flex items-center justify-between
                                      px-3.5 py-2.5 rounded-xl
                                      text-xs sm:text-sm font-semibold text-left
                                      transition-all duration-300
                                      ${
                                        isSelected
                                          ? "bg-orange-500 text-white shadow-md"
                                          : "bg-white text-gray-800 hover:bg-orange-50"
                                      }
                                    `}
                                  >
                                    <span>{mainCat.name}</span>
                                    <ChevronDown
                                      className={`w-4 h-4 transition-transform duration-300 ${
                                        isSelected
                                          ? "rotate-180 text-white"
                                          : "rotate-0 text-gray-400"
                                      }`}
                                    />
                                  </button>

                                  {/* SUB CATEGORIES FOR SELECTED MAIN CAT */}
                                  <div
                                    className={`accordion-grid ${
                                      isSelected
                                        ? "accordion-open mt-2 mb-1"
                                        : ""
                                    }`}
                                  >
                                    <div className="accordion-inner px-1">
                                      {loading ? (
                                        <div className="py-4 text-center text-gray-500 text-xs">
                                          Loading...
                                        </div>
                                      ) : (
                                        <div className="grid grid-cols-2 gap-2.5">
                                          {subCategories.map((collection) => (
                                            <div
                                              key={collection.id}
                                              onClick={() => {
                                                navigate(
                                                  `/collections/${collection.id}/${selectedMainCatId}`,
                                                );

                                                setIsOpen(false);
                                                setOpenMegaMenu(false);
                                              }}
                                              className="
                                                bg-white rounded-xl 
                                                border border-gray-100
                                                shadow-sm
                                                active:scale-95
                                                transition-all duration-300
                                                cursor-pointer
                                              "
                                            >
                                              {/* IMAGE */}
                                              <div
                                                className="
                                                  w-full h-[85px]
                                                  rounded-t-lg overflow-hidden
                                                  bg-gray-100
                                                "
                                              >
                                                <img
                                                  src={collection.image}
                                                  alt={collection.name}
                                                  className="w-full h-full object-cover"
                                                />
                                              </div>

                                              {/* TEXT */}
                                              <div className="pt-2 p-2.5">
                                                <h3
                                                  className="
                                                    text-xs text-center font-bold
                                                    text-gray-900 line-clamp-1
                                                  "
                                                >
                                                  {collection.name}
                                                </h3>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* CALL BUTTON - EXACTLY BELOW ALL MENU ITEMS */}
              <a
                href={`tel:${extraData?.phone}`}
                className="
                mt-2.5 flex items-center justify-center
                bg-orange-600 hover:bg-orange-700
                text-white font-semibold py-3 sm:py-3.5 rounded-xl text-sm sm:text-base
                shadow-sm transition-all duration-300
              "
              >
                Call Us : {extraData?.phone}
              </a>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};
